/* The formatted editor: the contenteditable inside the composer's box, and the toolbar above it.
   The typography mirrors the timeline's (modules/chat/render/chat.css) so what is typed looks like
   what will be read; every value is a token, and the dark theme swaps the tokens. */

.tb-editor__host {
	display: contents;
}

.tb-editor {
	/* The padding is `.tb-composer__input`'s (composer.css); what is added here is the one line the
	   box starts as and the cap it grows to. */
	min-height: calc(var(--target-min) + var(--space-2) + var(--space-1));
	max-height: 40dvh;
	overflow-y: auto;
	outline: none;
	color: var(--color-text);
	font: inherit;
	font-size: var(--text-md);
	line-height: var(--line-body);
	white-space: pre-wrap;
	overflow-wrap: anywhere;
}

/* An empty document shows the box's name where the text will go; the attribute is set by the
   composer and read by nothing else. */
.tb-editor[data-empty="true"]::before {
	content: attr(data-placeholder);
	position: absolute;
	color: var(--color-text-subtle);
	pointer-events: none;
}

.tb-editor > * + * {
	margin-block-start: var(--chat-flow-gap);
}

.tb-editor p,
.tb-editor h3,
.tb-editor h4,
.tb-editor h5,
.tb-editor h6 {
	margin: 0;
}

.tb-editor :is(h3, h4, h5, h6) {
	line-height: var(--line-md);
}

/* A heading inside prose is bigger than the author's name above it and no bigger than the room's
   title: `#` is --text-lg, `##` the one size outside the scale (18 px on the 24 px box, VISUAL.md
   §3.3), everything below them the body size in 600. The timeline draws the same three
   (modules/chat/render/chat.css) — what is typed has to look like what will be read. */

/* The three visible heading levels, as the timeline clamps them (spec 06). `#` is the one level with
   a line box of its own: 20 px on 28, the pair --text-lg wears everywhere else, so what is typed
   stands on the same line the reader will get (chat.css `.tb-body h3`). */
.tb-editor h3[data-level="1"] {
	font-size: var(--text-lg);
	font-weight: 700;
	line-height: var(--line-lg);
}

/* `##` — the one prose size outside the scale — is declared once for both prose surfaces, in
   `styles/base.css` beside the link rule, so the editor and the timeline cannot drift apart
   (X-B2 B2-19). Nothing about it is the editor's own. */

.tb-editor :is(h5, h6),
.tb-editor h3:not([data-level="1"]),
.tb-editor h4:not([data-level="2"]) {
	font-size: var(--text-md);
	font-weight: 600;
}

.tb-editor blockquote {
	margin: 0;
	border-inline-start: var(--rule-emphasis) solid var(--color-border);
	padding: 2px 0 2px var(--space-3);
	color: var(--color-text-muted);
}

.tb-editor ul,
.tb-editor ol {
	margin: 0;
	padding-inline-start: var(--space-5);
}

.tb-editor li > p {
	margin: 0;
}

/* A task item: the decorative box the schema draws stands in for the marker. */
.tb-editor li[data-checked] {
	list-style: none;
}

.tb-editor .tb-editor-task {
	display: inline-block;
	inline-size: 1.25em;
	margin-inline-start: -1.25em;
	color: var(--color-text-muted);
	user-select: none;
}

.tb-editor .tb-editor-task-body {
	display: inline;
}

.tb-editor pre {
	margin: 0;
	/* 10 / 12 / 12: the one padding in this file that is not a step of the scale, because the
	   timeline's code block is drawn to the same three numbers (VISUAL.md §7.4). */
	padding: 0.625rem var(--space-3) var(--space-3);
	border: 1px solid var(--color-border-subtle);
	border-radius: var(--radius-md);
	background: var(--color-code);
	line-height: 1.55;
	white-space: pre;
	overflow-x: auto;
}

.tb-editor pre code {
	font: inherit;
}

.tb-editor .tb-codespan {
	/* 1 px and 5 px: a mark inside a line of prose, the same box the timeline draws round one. */
	padding: 1px 0.3125rem;
	border: 1px solid var(--color-border-subtle);
	border-radius: var(--radius-sm);
	background: var(--color-code);
}

.tb-editor hr {
	border: 0;
	border-block-start: 1px solid var(--color-border-subtle);
}

.tb-editor table {
	border-collapse: collapse;
}

.tb-editor th,
.tb-editor td {
	border: 1px solid var(--color-border-subtle);
	padding: var(--space-1) var(--space-2);
}

.tb-editor th {
	font-weight: 600;
}

/* A picture is a chip: the composer never fetches one (F-2 D15). */
.tb-editor .tb-editor-image {
	display: inline-block;
	padding: 0 var(--space-2);
	border: 1px dashed var(--color-border);
	border-radius: var(--radius-sm);
	color: var(--color-text-muted);
	font-size: var(--text-sm);
}

/* An emoji run, wrapped by decorations.ts. The box inherits the interface font, which carries no
   colour emoji, so the face has to be named here the way the timeline names it (chat.css) — typed
   and posted are then the same picture instead of a tofu box and a party popper. */
.tb-editor .tb-emoji {
	font-family: var(--font-emoji);
	font-style: normal;
}

/* The formatting row, on the composer's tool row: `display: contents` is what the composer's fold
   needs, so this element only exists to be a row when it stands anywhere else. The buttons are
   `ui/`'s ghost quiet icon squares and the on-paint is the tool row's (composer.css), so nothing
   about a tool is drawn twice. */
.tb-editor__toolbar {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 2px;
}

/* Between the marks and the two doors beside them: a hairline the height of the glyphs, not of the
   button. */
.tb-editor__sep {
	inline-size: 1px;
	block-size: 1.25rem;
	margin: 0 var(--space-1);
	background: var(--color-border-subtle);
}

.tb-editor__link-form {
	display: flex;
	flex-direction: column;
	gap: var(--space-3);
}
